import { ChevronDown, FileText, Plus } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { V3_MENU } from "@/components/ui/redesign/v3";
import type { RecorderNoteSettings } from "@/hooks/visits/useRecorderNoteSettings";
import {
  EXAMINATIONS,
  type Examination,
  hasNoteOptions,
} from "@/lib/recorder-note-settings";
import type { TemplateChoice } from "@/lib/template-preferences";
import { cn } from "@/lib/utils";

const ITEM =
  "min-h-11 rounded-lg py-2 pr-3 text-[14px] text-v3-text focus:bg-v3-surface-2 focus:text-v3-text lg:min-h-9";
const GROUP_LABEL =
  "px-3 pb-1 pt-2 text-[12px] font-semibold uppercase tracking-wide text-v3-text-3";

/**
 * The note template on the v3.2 recorder: Stop writes the note with it, so it
 * is chosen here (before or while recording) instead of on a sheet after
 * Stop. Built-in templates also take the examination (in person or virtual).
 */
export const RecorderTemplateChip = ({
  settings,
  className,
  onCreateTemplate,
}: {
  settings: RecorderNoteSettings;
  className?: string;
  /** Before recording: "Create your own template" (from notes or blank). */
  onCreateTemplate?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const examLabel: Record<Examination, string> = {
    Physical: t("noteOptions.exam.physical"),
    Virtual: t("noteOptions.exam.virtual"),
  };
  const options = hasNoteOptions(settings.template);
  const group = (label: string, choices: TemplateChoice[]) =>
    choices.length > 0 && (
      <>
        <DropdownMenuLabel className={GROUP_LABEL}>{label}</DropdownMenuLabel>
        {choices.map((choice) => (
          <DropdownMenuRadioItem
            key={choice.value}
            className={ITEM}
            value={choice.value}
          >
            <span className="min-w-0 truncate">{choice.name}</span>
          </DropdownMenuRadioItem>
        ))}
      </>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={t("recorderV32.template.aria", {
            name: settings.templateName,
          })}
          className={cn(
            "inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-full bg-v3-panel px-4 text-[14px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-60",
            className,
          )}
          data-testid="recorder-template-chip"
          disabled={!settings.ready}
          type="button"
        >
          <FileText aria-hidden className="h-[18px] w-[18px] shrink-0" />
          <span className="min-w-0 truncate">{settings.templateName}</span>
          {options && settings.examination === "Virtual" && (
            <span className="shrink-0 font-medium text-v3-text-3">
              · {examLabel.Virtual}
            </span>
          )}
          <ChevronDown
            aria-hidden
            className="h-4 w-4 shrink-0 text-v3-text-3"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        // Phones: the 16px page gutter on both sides, never the screen edge.
        collisionPadding={16}
        className={cn(
          V3_MENU,
          "max-h-[min(70dvh,480px)] w-[min(320px,calc(100vw-32px))] overflow-y-auto p-1",
        )}
        side="top"
      >
        <DropdownMenuRadioGroup
          value={settings.template}
          onValueChange={settings.setTemplate}
        >
          {group(t("templates.favorites.group"), settings.favorites)}
          {group(
            t("components.recorderForm.defaultTemplatesSelectLabel"),
            settings.builtIn,
          )}
          {group(
            t("components.recorderForm.customTemplatesSelectLabel"),
            settings.custom,
          )}
        </DropdownMenuRadioGroup>
        {onCreateTemplate && (
          <DropdownMenuItem
            className={cn(ITEM, "items-start gap-2.5 pl-3")}
            data-testid="recorder-create-template"
            onSelect={onCreateTemplate}
          >
            <Plus aria-hidden className="mt-0.5 size-4 shrink-0" />
            <span className="min-w-0">
              <span className="block font-medium">
                {t("templates.createOwn.title")}
              </span>
              <span className="block text-[12.5px] text-v3-text-3">
                {t("templates.createOwn.hint")}
              </span>
            </span>
          </DropdownMenuItem>
        )}
        {options && (
          <>
            <DropdownMenuSeparator className="bg-v3-border" />
            <DropdownMenuLabel className={GROUP_LABEL}>
              {t("components.recorderForm.examinationLabel")}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={settings.examination}
              onValueChange={(value) => {
                settings.setExamination(value as Examination);
              }}
            >
              {EXAMINATIONS.map((value) => (
                <DropdownMenuRadioItem
                  key={value}
                  className={ITEM}
                  value={value}
                >
                  {examLabel[value]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
